<template>
  <ControlWrapper v-bind="controlWrapper" :styles="styles" :is-focused="isFocused" :applied-options="appliedOptions">
    <section id="widget-confirm" class="widget-confirm-step">
      <div
        v-if="submissionReference.reference && !isErrorSubmission"
        class="widget-confirm-step_banner"
        :class="config.theme?.components?.banner"
      >
        <WidgetIcon :icon="themeIcons.info" class="widget-confirm-step_banner--icon" width="24" />
        <span class="body1-bold">{{ submissionReference.title }}:</span>
        <span class="widget-confirm-step_banner_reference">
          {{ submissionReference.reference }}
        </span>
      </div>
      <div class="widget-confirm-step_wrapper">
        <WidgetIcon :icon="settings.icon" class="widget-primary-icon" />
        <div class="default-gap flex w-full flex-col items-center text-center">
          <div class="widget-confirm-step_header">
            <h2 class="widget-confirm-step_header--title" :class="componentStyles?.title">{{ settings.title }}</h2>
            <p class="widget-confirm-step_header--description">
              {{ settings.subtitle }}
            </p>
          </div>

          <ContactInfoCard v-if="settings?.callUs?.text && !isReferral" v-bind="callUsContactConfig"></ContactInfoCard>

          <div
            v-if="!isRecoveryRequired && settings?.hours && openHours?.length"
            class="w-full rounded-custom border border-backgroundSoft-600 p-4"
          >
            <h2 class="widget-confirm-step_hours--title">{{ settings.hours.title }}</h2>
            <div class="flex flex-col gap-1">
              <div v-for="({ open, close }, index) in openHours" :key="index" class="flex w-full justify-between">
                <p class="widget-confirm-step_hours--description">{{ WEEK_DAYS[index] }}</p>
                <p v-if="open">{{ open }} - {{ close }}</p>
                <p v-else>CLOSED</p>
              </div>
            </div>
          </div>

          <div v-if="settings.title2 || settings?.subtitle2" class="widget-confirm-step_header">
            <h2 v-if="settings.title2" class="widget-confirm-step_header--title" :class="componentStyles?.title">
              {{ settings.title2 }}
            </h2>
            <p v-if="settings?.subtitle2" class="widget-confirm-step_header--description">
              {{ settings.subtitle2 }}
            </p>
          </div>
          <div v-if="progressList?.length" class="widget-confirm-step_body">
            <div class="widget-confirm-step_body--child" v-for="(item, index) in progressList" :key="index">
              <div class="widget-confirm-step_body--list-item-wrapper">
                <WidgetIcon
                  :icon="item.checked ? `${themeIcons.checkCircle}` : `${themeIcons.circle}`"
                  width="1.8125rem"
                  class="widget-confirm-step_body--list-item"
                />
                <div
                  v-if="index !== progressList.length - 1 && checkTheme('default')"
                  class="widget-confirm-step_body--checklist-line"
                ></div>
              </div>
              <span class="widget-confirm-step_body--text">{{ item.text }}</span>
            </div>
          </div>
          <div
            v-if="isReferral && !isReferenceHidden && (axicloudLead?.reference || leadUrl)"
            class="widget-confirm-step_lead"
          >
            <div v-if="axicloudLead?.reference" class="widget-confirm-step_lead--banner">
              <WidgetIcon :icon="themeIcons.info" class="widget-confirm-step_lead--icon" width="24" />
              <span class="body1-bold">Axicloud Lead Reference:</span>
              <span class="widget-confirm-step_lead_reference">
                {{ axicloudLead?.reference }}
              </span>
            </div>
            <WButton v-if="leadUrl" type="outline" class="widget-form-renderer_lead" @click="navigateTo(leadUrl, true)">
              {{ infoSection.callbackBtnText || 'Continue' }}
            </WButton>
          </div>
          <div
            class="text-start"
            v-if="
              submission && submission?.stepType === StepType.ASSISTANCE && submission.repairLocationLight?.latitude
            "
          >
            <h2 class="widget-confirm-step_header--title" :class="componentStyles?.title">
              Your selected Collision Center:
            </h2>

            <RepairCard :location="submission.repairLocationLight" :editable="false"> </RepairCard>
          </div>

          <div
            v-if="isRecoveryRequired && settings?.hours && openHours?.length"
            class="w-full rounded-custom border border-backgroundSoft-600 p-4"
          >
            <h2 class="widget-confirm-step_hours--title">{{ settings.hours.title }}</h2>
            <div class="flex flex-col gap-1">
              <div v-for="({ open, close }, index) in openHours" :key="index" class="flex w-full justify-between">
                <p class="widget-confirm-step_hours--description">{{ WEEK_DAYS[index] }}</p>
                <p v-if="open">{{ open }} - {{ close }}</p>
                <p v-else>CLOSED</p>
              </div>
            </div>
          </div>
          <div class="widget-confirm-step_info" v-if="infoSection?.text">
            <div class="flex h-full items-center justify-center">
              <WidgetIcon
                v-if="infoSection.icon"
                :icon="infoSection.icon"
                class="widget-primary-icon mr-2 font-bold"
                width="24"
              />
            </div>
            <div>
              <p class="body1 text-fontPrimary-500">{{ infoSection.text }}</p>
            </div>
          </div>
        </div>
      </div>
      <WButton
        v-if="config.callbackUrl || config.learnMoreUrl"
        type="primary"
        class="widget-form-renderer_submit"
        @click="navigateTo(config.callbackUrl || config.learnMoreUrl, !config.callbackUrl && !!config.learnMoreUrl)"
      >
        {{ infoSection.callbackBtnText || 'Continue' }}
      </WButton>

      <div
        v-if="!checkTheme('default') && config.policyHubUrl"
        class="mt-8 flex w-full justify-center md:mt-20 md:justify-start"
      >
        <WButton class="bg-primary px-20 text-white" @click="navigateTo(config.policyHubUrl)">
          Back to your Policy
        </WButton>
      </div>

      <ContactInfoCard v-if="!isRepairForm && !isReferral" v-bind="defaultContactConfig"></ContactInfoCard>
    </section>
  </ControlWrapper>
</template>

<script lang="ts" setup>
  import { ControlElement } from '@jsonforms/core';

  import { rendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import { default as ControlWrapper } from './ControlWrapper.vue';
  import { useWidgetControl } from '../util';
  import { ref, nextTick, computed } from 'vue';
  import { config, themeIcons, checkTheme, getComponentStyle } from 'src/utils/config';
  import { Ref, inject, onMounted } from 'vue';
  import WidgetIcon from '../../base/WidgetIcon.vue';
  import WButton from 'src/components/base/Button.vue';
  import ContactInfoCard from '../../claims-widget/components/ContactInfoCard.vue';
  import { StepType } from 'src/index';
  import { Submission } from 'src/types/api';
  import RepairCard from './RepairFinder/RepairCard.vue';

  const WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  type Lead = {
    reference: string;
    name: string;
    id: string;
    status: string;
    resource_url?: string;
  };
  export type ConfirmStepConfig = {
    title: string;
    title2?: string;
    paragraphs?: { text: string; checked: boolean }[];
    callUs?: { text?: string };
    action: string;
    icon: string;
    referenceLabel?: string;
    subtitle: string;
    subtitle2?: string;
    info: { text: string; icon?: string; callbackBtnText?: string };
    roadWorthy: { text: string; icon?: string };
    hours?: { title: string; integration: string };
  };
  const props = defineProps({
    ...rendererProps<ControlElement>(),
  });
  const componentStyles = getComponentStyle();
  const { control, controlWrapper, styles, isFocused, appliedOptions } = useWidgetControl(useJsonFormsControl(props));
  const submission = ref<Partial<Submission>>(
    control.value.data || config.value.sessionData.submission || { reference: config.value.reference }
  );
  const settings = (control.value.schema as unknown as { confirmStep: ConfirmStepConfig }).confirmStep;
  const rootElement = inject<Ref<HTMLDivElement>>(config.value.rootKey);
  const vehicleRoadWorthy = computed(
    () => submission.value.stepType === StepType.CLAIMS && submission.value?.vehicleRoadWorthy
  );
  const needRecovery = computed(() => ('needRecovery' in submission.value ? submission.value.needRecovery : undefined));
  const isRecoveryRequired = computed(() => vehicleRoadWorthy.value === false || !!needRecovery.value);
  const isRepairForm = computed(
    () => submission.value?.stepKey?.includes?.('repair-form') || submission.value.stepKey === 'repair-finder-form-us'
  );
  const axicloudLead = computed(
    () =>
      (submission.value?.integrations && !('error' in submission.value.integrations)
        ? submission.value?.integrations?.axicloudLead
        : {}) as Lead
  );
  const leadUrl = computed(() => axicloudLead?.value?.resource_url);
  const isErrorSubmission = computed(() => !!submission.value?.rejectedIntegrations?.length);
  const reference = computed(() => {
    if (
      submission.value?.integrations &&
      Object.keys(submission.value?.integrations)?.length &&
      !('error' in submission.value.integrations)
    ) {
      if (Object.keys(submission.value?.integrations).includes('brokerDirect')) {
        return submission.value?.integrations?.brokerDirect?.reference || submission.value?.reference || '';
      }
      if (Object.keys(submission.value?.integrations).includes('axicloudLead')) {
        return submission.value?.integrations?.axicloudLead?.reference || submission.value?.reference || '';
      }
    }
    return submission.value?.reference || '';
  });

  const submissionReference = computed(() => {
    if (!submission.value) {
      return {
        reference: '',
        title: '',
      };
    }

    if (submission.value.stepType === StepType.REFERRAL && !isReferenceHidden.value && axicloudLead.value) {
      //   return {
      //     reference: submission.value?.reference,
      //     title: 'Axicloud Lead Reference',
      //   };
      return {
        reference: '',
        title: '',
      };
    }
    return {
      reference: reference.value,
      title: settings?.referenceLabel || 'Claim Reference',
    };
  });
  const progressList = settings.paragraphs;
  const infoSection = settings.info;

  const openHours =
    config.value.settings?.businessHours ||
    config.value.integrations?.find(({ name }) => name === settings.hours?.integration)?.settings?.businessHours;

  function navigateTo(url?: string, newTab = false) {
    if (!url) return;
    if (newTab) {
      window.open(url, '_blank');
      return;
    }
    window.location.replace(url);
  }

  const isReferral = computed(() => config.value.settings?.type === StepType.REFERRAL);

  const isReferenceHidden = computed(
    () => submission.value.stepKey === 'retailer-referral-form' || submission.value.stepKey === 'retailer-repair-form'
  );

  onMounted(() => {
    nextTick(() => {
      rootElement?.value.scrollTo(0, 0);
      document.scrollingElement?.scrollTo(0, 0);
      window.scrollTo(0, 0);
    });
  });

  const defaultContactConfig = {
    title: 'Need immediate assistance?',
    description: `If you need immediate recovery, call us and we'll guide you through the best steps for your circumstances`,
    phone: {
      title: 'Call us for help',
    },
  };

  const callUsContactConfig = {
    phone: {
      title: 'Call us 24/7',
    },
  };
</script>
